import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { AdminNavigation } from "@/components/layout/Navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import { 
  Sun, 
  Plus, 
  Edit, 
  Trash2, 
  Search,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Flame,
  Compass,
  Target,
  Quote,
  ArrowLeft,
  Phone,
  Users
} from "lucide-react";

const QUARTER_CONFIG = {
  wake_up: { label: "Wake Up", icon: Sparkles, color: "text-yellow-400", description: "Awareness, patterns, identity, observation" },
  burn_the_map: { label: "Burn the Map", icon: Flame, color: "text-orange-400", description: "Disruption, letting go, questioning, courage" },
  whole: { label: "Whole", icon: Compass, color: "text-green-400", description: "Integration, boundaries, alignment, self-trust" },
  purposeful_paths: { label: "Purposeful Paths", icon: Target, color: "text-blue-400", description: "Direction, contribution, purpose, building future" }
};

export default function AdminSignals() {
  const navigate = useNavigate();
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterQuarter, setFilterQuarter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingSignal, setEditingSignal] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    day_number: 1,
    quarter: "wake_up",
    title: "",
    prompt: "",
    quote: "",
    quote_author: ""
  });
  const [signalTargets, setSignalTargets] = useState([]);
  const [showTargetsDialog, setShowTargetsDialog] = useState(false);

  const fetchSignalTargets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/signal-targets`, {
        withCredentials: true
      });
      setSignalTargets(response.data.targets || []);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load signal targets");
      }
    }
  }, []);

  const fetchSignals = useCallback(async () => {
    try {
      const params = { page, limit: 20 };
      if (filterQuarter !== "all") params.quarter = filterQuarter;
      
      const response = await axios.get(`${API}/admin/signals`, {
        withCredentials: true,
        params
      });
      setSignals(Array.isArray(response.data?.signals) ? response.data.signals : []);
      setTotalPages(response.data?.pages || 1);
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error("Failed to load signals");
      }
    } finally {
      setLoading(false);
    }
  }, [page, filterQuarter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/signals/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      // Stats are supplementary - graceful degradation without error display
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch signal stats:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchSignals();
    fetchSignalTargets();
    fetchStats();
  }, [fetchSignals, fetchSignalTargets, fetchStats]);

  const handleSaveSignal = async () => {
    try {
      if (editingSignal) {
        await axios.put(`${API}/admin/signals/${editingSignal.signal_id}`, formData, {
          withCredentials: true
        });
        toast.success("Signal updated");
      } else {
        await axios.post(`${API}/admin/signals`, formData, {
          withCredentials: true
        });
        toast.success("Signal created");
      }
      setShowAddDialog(false);
      setEditingSignal(null);
      resetForm();
      fetchSignals();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save signal");
    }
  };

  const handleDeleteSignal = async (signalId) => {
    if (!confirm("Are you sure you want to delete this signal?")) return;
    
    try {
      await axios.delete(`${API}/admin/signals/${signalId}`, {
        withCredentials: true
      });
      toast.success("Signal deleted");
      fetchSignals();
      fetchStats();
    } catch {
      toast.error("Failed to delete signal");
    }
  };

  const openEditDialog = (signal) => {
    setEditingSignal(signal);
    setFormData({
      day_number: signal.day_number,
      quarter: signal.quarter,
      title: signal.title,
      prompt: signal.prompt,
      quote: signal.quote || "",
      quote_author: signal.quote_author || ""
    });
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      day_number: 1,
      quarter: "wake_up",
      title: "",
      prompt: "",
      quote: "",
      quote_author: ""
    });
  };

  const filteredSignals = signals.filter(signal =>
    signal.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    signal.prompt.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminNavigation />
        <main className="pt-24 pb-12 px-4 max-w-7xl mx-auto">
          <div className="text-center text-foreground/60">Loading signals...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminNavigation />
      
      <main className="pt-20 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <header className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/admin")}
                className="h-10 w-10 p-0 hover:bg-muted"
                data-testid="signals-back-btn"
              >
                <ArrowLeft className="w-5 h-5" style={{ color: '#a69373' }} />
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-heading font-bold mb-2" style={{ color: '#a69373' }}>Daily Signals</h1>
                <p style={{ color: 'rgba(166, 147, 115, 0.7)' }}>Manage the 365-day reflection journey</p>
              </div>
            </div>
            <Dialog open={showAddDialog} onOpenChange={(open) => {
              setShowAddDialog(open);
              if (!open) {
                setEditingSignal(null);
                resetForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Signal
                </Button>
              </DialogTrigger>

            {/* SMS/WhatsApp Targets Dialog */}
            <Dialog open={showTargetsDialog} onOpenChange={(open) => {
              setShowTargetsDialog(open);
              if (open) fetchSignalTargets();
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-[#a69373]/30 text-[#a69373] hover:bg-[#a69373]/10">
                  <Phone className="w-4 h-4 mr-2" />
                  SMS/WhatsApp Targets
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#f5f9f5] border-[#c5d5c5] max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-[#1a1a1a] flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    SMS/WhatsApp Signal Targets
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-4">
                  <p className="text-sm text-[#1a1a1a]/70 mb-4">
                    Users who have provided phone numbers for daily signal notifications:
                  </p>
                  {signalTargets.length === 0 ? (
                    <div className="text-center py-8 text-[#1a1a1a]/50">
                      <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No users have added phone numbers yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="bg-[#7b9e7b]/10 rounded-lg p-3 mb-4">
                        <p className="text-sm font-medium text-[#1a1a1a]">
                          Total: {signalTargets.length} users with phone numbers
                        </p>
                      </div>
                      {signalTargets.map((target) => (
                        <div 
                          key={target.user_id}
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-[#c5d5c5]"
                        >
                          <div>
                            <p className="font-medium text-[#1a1a1a]">{target.name}</p>
                            <p className="text-xs text-[#1a1a1a]/60">{target.email}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-sm text-[#1a1a1a]">{target.full_phone}</p>
                            <p className="text-xs text-[#7b9e7b] capitalize">{target.subscription_type}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
              <DialogContent className="bg-emerald-900 border-emerald-800 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white">
                    {editingSignal ? "Edit Signal" : "Add New Signal"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-white/70 text-sm mb-1 block">Day Number</label>
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        value={formData.day_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, day_number: parseInt(e.target.value) || 1 }))}
                        className="bg-white/10 border-white/20 text-white"
                        disabled={editingSignal}
                      />
                    </div>
                    <div>
                      <label className="text-white/70 text-sm mb-1 block">Quarter</label>
                      <Select 
                        value={formData.quarter} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, quarter: value }))}
                      >
                        <SelectTrigger className="bg-white/10 border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(QUARTER_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-white/70 text-sm mb-1 block">Title</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Morning Awareness"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-white/70 text-sm mb-1 block">Reflection Prompt</label>
                    <Textarea
                      value={formData.prompt}
                      onChange={(e) => setFormData(prev => ({ ...prev, prompt: e.target.value }))}
                      placeholder="What question will guide today's reflection?"
                      className="bg-white/10 border-white/20 text-white min-h-[80px]"
                    />
                  </div>
                  
                  <div>
                    <label className="text-white/70 text-sm mb-1 block">Quote (Optional)</label>
                    <Input
                      value={formData.quote}
                      onChange={(e) => setFormData(prev => ({ ...prev, quote: e.target.value }))}
                      placeholder="An inspirational quote"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="text-white/70 text-sm mb-1 block">Quote Author (Optional)</label>
                    <Input
                      value={formData.quote_author}
                      onChange={(e) => setFormData(prev => ({ ...prev, quote_author: e.target.value }))}
                      placeholder="e.g., Marcus Aurelius"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button 
                      variant="outline" 
                      className="flex-1 border-white/20 text-white"
                      onClick={() => {
                        setShowAddDialog(false);
                        setEditingSignal(null);
                        resetForm();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      className="flex-1 btn-primary"
                      onClick={handleSaveSignal}
                      disabled={!formData.title || !formData.prompt}
                    >
                      {editingSignal ? "Update" : "Create"} Signal
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </header>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <Card className="glass-card">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-heading text-primary mb-1">{stats.total_signals}</div>
                <div className="text-xs text-[#a69373]/60">Total Signals</div>
              </CardContent>
            </Card>
            <Card className="glass-card">
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-heading text-[#a69373] mb-1">{stats.total_responses}</div>
                <div className="text-xs text-[#a69373]/60">Total Responses</div>
              </CardContent>
            </Card>
            {stats.by_quarter?.slice(0, 2).map((q) => {
              const config = QUARTER_CONFIG[q.quarter];
              return (
                <Card key={q.quarter} className="glass-card">
                  <CardContent className="p-4 text-center">
                    <div className={`text-2xl font-heading ${config?.color || 'text-[#a69373]'} mb-1`}>{q.count}</div>
                    <div className="text-xs text-[#a69373]/60">{config?.label || q.quarter}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/40" />
            <Input
              placeholder="Search signals..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-foreground/20"
            />
          </div>
          <Select value={filterQuarter} onValueChange={(value) => {
            setFilterQuarter(value);
            setPage(1);
          }}>
            <SelectTrigger className="w-full sm:w-48 bg-white/5 border-foreground/20">
              <SelectValue placeholder="Filter by quarter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Quarters</SelectItem>
              {Object.entries(QUARTER_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Signals List */}
        <div className="space-y-3">
          {filteredSignals.map((signal) => {
            const quarterConfig = QUARTER_CONFIG[signal.quarter];
            const QuarterIcon = quarterConfig?.icon || Sun;
            
            return (
              <Card key={signal.signal_id} className="glass-card">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-white/10 shrink-0`}>
                      <QuarterIcon className={`w-5 h-5 ${quarterConfig?.color || 'text-white'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-white/40">Day {signal.day_number}</span>
                        <span className="text-white/20">•</span>
                        <span className={`text-xs ${quarterConfig?.color || 'text-white/60'}`}>
                          {quarterConfig?.label || signal.quarter}
                        </span>
                      </div>
                      <h3 className="text-white font-medium mb-1">{signal.title}</h3>
                      <p className="text-white/60 text-sm line-clamp-2">{signal.prompt}</p>
                      {signal.quote && (
                        <div className="flex items-start gap-1 mt-2">
                          <Quote className="w-3 h-3 text-white/30 shrink-0 mt-0.5" />
                          <p className="text-white/40 text-xs italic line-clamp-1">
                            "{signal.quote}" {signal.quote_author && `— ${signal.quote_author}`}
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-white/60 hover:text-white hover:bg-white/10"
                        onClick={() => openEditDialog(signal)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-400/60 hover:text-red-400 hover:bg-red-400/10"
                        onClick={() => handleDeleteSignal(signal.signal_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="border-white/20 text-white"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-white/60 text-sm">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="border-white/20 text-white"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
